import { requestGQL } from 'common/graphQlClient';
import { useQueryClient } from 'react-query';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';

import { UPDATE_TPOUTLETS_PHOTOS } from '../api';

type UploadTpOutletPhotoPayload = {
  propertyId: number;
  photoUrl: string;
  tpOutletId: string;
};

/**
 * Hook for updating TP outlet photos with offline support.
 *
 * When offline, the mutation is queued and will be sent when back online.
 * The photo URL update is stored locally and synced later.
 */
export const useMutateTpOutletPhoto = (propertyId: number | null) => {
  const queryClient = useQueryClient();

  const mutation = useOfflineMutation<unknown, { message: string }, UploadTpOutletPhotoPayload>({
    mutationFn: async (payload) => requestGQL(UPDATE_TPOUTLETS_PHOTOS, payload),
    mutationName: 'uploadTpOutletPhoto',
    graphqlDocument: UPDATE_TPOUTLETS_PHOTOS,
    entity: 'tpOutletPhoto',
    operationType: 'UPDATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [['propertyConnection', propertyId]],
    // Optimistic update - add photo URL to local cache immediately
    getOptimisticData: (payload) => payload,
    applyOptimisticUpdate: (optimisticData) => {
      const data = optimisticData as UploadTpOutletPhotoPayload;
      const cacheKey = ['propertyConnection', propertyId];

      queryClient.setQueryData(cacheKey, (oldData: any) => {
        if (!oldData?.propertyById?.connectionByConnectionId?.serialized2?.nodeDataArray) {
          return oldData;
        }

        // Find and update the TP outlet with the new photo
        const nodeDataArray = oldData.propertyById.connectionByConnectionId.serialized2.nodeDataArray.map(
          (node: any) => {
            if (node.id === data.tpOutletId && node.model === 'tpOutlet') {
              return {
                ...node,
                cfObject: {
                  ...node.cfObject,
                  images: [...(node.cfObject?.images || []), data.photoUrl],
                },
              };
            }
            return node;
          },
        );

        return {
          ...oldData,
          propertyById: {
            ...oldData.propertyById,
            connectionByConnectionId: {
              ...oldData.propertyById.connectionByConnectionId,
              serialized2: {
                ...oldData.propertyById.connectionByConnectionId.serialized2,
                nodeDataArray,
              },
            },
          },
        };
      });
    },
  });

  return mutation;
};
